import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX, RefObject } from 'preact';
import { useDateFormat, useLocale, useTranslation, useRelativeTime } from '../i18n';
import {
    PageContainer,
    PageBody,
    ErrorTile,
    Skeleton,
    Eyebrow,
    SourceFavicon,
    WorkCard as UiWorkCard,
} from '../primitives';
import { useFetch } from '../hooks/useFetch';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { toCardModel } from '../components/toCardModel';
import { CollectionsButton } from '../components/CollectionsButton';
import { useWorkLike } from '../hooks/useMyLikes';
import { workUrl, withBase, gwpackageUrl } from '../paths';
import { LINKS } from '../brand';
import type { Work, Sighting, WorkSummary, Provider } from '../api/types';
import { resolveSource } from '../api/sourceRegistry';
import { isAdmin, useMe, signInPath } from '../auth/useMe';
import { ConfirmModal } from '../primitives/admin';

/**
 * v2 package detail page (ADR-0025). Surfaces the gallery + byline + two-button install model;
 * conditional status notices (archive / noredist / paywall) and the "available on" panel for
 * multi-source works. Stats and Related sections close out the page.
 */
export function WorkDetailPage({
    params,
}: {
    params: { id?: string; slug?: string };
}) {
    const t = useTranslation();
    const rel = useRelativeTime();
    const { apiSet } = useLocale();
    const me = useMe();
    const viewerIsAdmin = me.status === 'authenticated' && isAdmin(me.user);

    // The route is /work/{id}/{slug} (the trailing slug is decorative) or the
    // bare /work/{id}; either way the id is the resolution key.
    const requestedId = params.id ?? '';

    // Bumped after admin mutations (suppress / pin / recrawl) so the page
    // re-fetches the Work + Sightings without a hard reload.
    const [refreshKey, setRefreshKey] = useState(0);
    const refresh = () => setRefreshKey((n) => n + 1);

    const pkg = useFetch(() => api.works.get(requestedId, apiSet), [requestedId, apiSet, refreshKey]);
    const sightings = useFetch(() => api.works.sightings(requestedId), [requestedId, refreshKey]);
    const sources = useFetch(() => api.providers.list(), []);

    // Related works are precomputed server-side (the RelatedWork sidecar): one cheap ranked,
    // visibility-filtered read instead of walking the whole catalog to rank in the browser.
    // The endpoint resolves the full id (or a retired alias), so the URL id is fine here.
    const related = useFetch(() => api.works.related(requestedId, 10, apiSet), [requestedId, apiSet]);

    const sourcesBySlug = useMemo(() => {
        if (sources.status !== 'success') return undefined;
        return new Map(sources.data.map((s) => [s.slug, s]));
    }, [sources]);

    // Canonicalize the URL when the fetch resolved through a 301 (the requested
    // id was a RetiredWorkId alias and the server redirected to the survivor),
    // OR when the user landed on the bare /work/{id} form and the package has a
    // slug - we swap to /work/{id}/{slug} via history.replaceState (no stack
    // entry). Called unconditionally ABOVE the loading/error guards so the hook
    // order is stable across renders; it no-ops until the package resolves.
    useCanonicalUrl(pkg.status === 'success' ? pkg.data : undefined);

    if (pkg.status === 'loading') {
        return (
            <Layout>
                <PageBody rhythm="page">
                    <Skeleton.Hero />
                </PageBody>
            </Layout>
        );
    }

    if (pkg.status === 'error') {
        return (
            <Layout>
                <PageContainer cap="readable">
                    <ErrorTile title={t('common.error.notFoundTitle')} details={pkg.error.message}>
                        <a href={withBase('/')} class="inline-block text-accent">
                            {t('work.breadcrumbCatalog')} ←
                        </a>
                    </ErrorTile>
                </PageContainer>
            </Layout>
        );
    }

    if (pkg.status !== 'success') return null;
    const p = pkg.data;

    return (
        <Layout>
            <BreadcrumbBar pkg={p} />

            {p.status === 'Tombstoned' && <TombstonedBanner tombstonedAt={p.tombstonedAt ?? null} />}

            {viewerIsAdmin && p.status === 'Suppressed' && <SuppressionAdminStripe />}

            <PageBody rhythm="page">
                <HeroSection pkg={p} rel={rel} />

                <AvailableOnPanel pkg={p} sourcesBySlug={sourcesBySlug} />

                <SightingsList pkg={p} sightings={sightings} sourcesBySlug={sourcesBySlug} />

                <AdminPanel
                    pkg={p}
                    sightings={sightings.status === 'success' ? sightings.data ?? [] : []}
                    onMutated={refresh}
                />

                <RelatedSection
                    pkg={p}
                    related={related.status === 'success' ? related.data : []}
                />
            </PageBody>
        </Layout>
    );
}

function BreadcrumbBar({ pkg }: { pkg: Work }) {
    const t = useTranslation();
    return (
        <div class="border-b border-rule bg-surface-veil">
            <div class="mx-auto flex max-w-[1680px] items-center gap-2 px-[clamp(20px,3.5vw,56px)] py-2.5">
                <a
                    href={withBase('/')}
                    class="inline-flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.16em] text-fg-muted transition-colors hover:text-fg-primary"
                >
                    ← {t('work.breadcrumbCatalog')}
                </a>
                <span class="font-mono text-[10.5px] uppercase tracking-[0.16em] text-fg-faint">
                    /
                </span>
                <span class="truncate font-mono text-[10.5px] uppercase tracking-[0.16em] text-fg-muted">
                    {pkg.name ?? '(unnamed)'}
                </span>
            </div>
        </div>
    );
}

/**
 * Top-edge ribbon shown to admin viewers when a Work is Suppressed -
 * either auto-applied (upstream Decline intent) or admin-set. Mirrors the
 * dashed border treatment on card/row primitives so a moderator scanning
 * the catalog and landing on the detail page sees the same lifecycle
 * signal in a louder form.
 *
 * <para>Post ADR-0022 correction (A.6): Works have only three statuses
 * (Published / Suppressed / Tombstoned). The pre-publish "Discovered"
 * variant of this banner is gone - that state was an artifact of the
 * pre-A.2+A.4 1:1 mint-at-Discover model.</para>
 */
function SuppressionAdminStripe() {
    const t = useTranslation();
    return (
        <div class="border-b border-dashed bg-state-failed/15 text-state-failed border-state-failed/40" role="status">
            <div class="mx-auto flex max-w-[1680px] items-center gap-2 px-[clamp(20px,3.5vw,56px)] py-1.5">
                <span class="font-mono text-[10px] uppercase tracking-[0.18em]">Suppressed</span>
                <span class="text-[11.5px]">{t('work.lifecycleBanner.suppressed')}</span>
            </div>
        </div>
    );
}

/**
 * Public-visible banner for a tombstoned Work: every Sighting was
 * Removed, but per ADR-0022 Phase 2 the row stays so the URL still
 * resolves. Renders as an "archived, no longer upstream" notice for all
 * viewers (anonymous + authenticated + admin) - distinct from the
 * admin-only LifecycleAdminStripe because tombstone is part of the
 * public lifecycle, not a moderation state.
 */
function TombstonedBanner({ tombstonedAt }: { tombstonedAt: string | null }) {
    const rel = useRelativeTime();
    const ago = tombstonedAt ? rel(tombstonedAt) : null;
    return (
        <div
            class="border-b border-dashed border-rule bg-surface-veil"
            role="status"
            aria-label="Archived package"
        >
            <div class="mx-auto flex max-w-[1680px] items-center gap-2 px-[clamp(20px,3.5vw,56px)] py-1.5">
                <span class="font-mono text-[10px] uppercase tracking-[0.18em] text-fg-muted">
                    Archived
                </span>
                <span class="text-[11.5px] text-fg-muted">
                    No longer hosted upstream{ago ? ` (since ${ago})` : ''}. Mirror bytes remain
                    available if the package was mirrored.
                </span>
            </div>
        </div>
    );
}

/**
 * Update the browser URL bar to the canonical Work id when the server
 * resolved a request through the resolution map (a retired id 301'd to
 * the survivor). The fetch follows the 301 transparently, so the SPA
 * receives the survivor row but the URL bar still shows the requested id;
 * this hook swaps it via history.replaceState (no new history entry -
 * the user didn't navigate, the server did). Cheap, idempotent, no-op when
 * the ids already match.
 */
function useCanonicalUrl(pkg: Work | undefined) {
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!pkg?.id) return;
        const canonical = workUrl({ id: pkg.id, slug: pkg.slug });
        const current = window.location.pathname;
        if (current === canonical) return;
        // Preserve query string + hash if any.
        const url = new URL(window.location.href);
        url.pathname = canonical;
        window.history.replaceState(window.history.state, '', url.toString());
    }, [pkg?.id, pkg?.slug]);
}

function HeroCover({ pkg }: { pkg: Work }) {
    const t = useTranslation();
    const isPaywall = pkg.install.kind === 'Paywalled';

    const mediaIds = useMemo(
        () =>
            [pkg.media.images.cover, ...(pkg.media.images.additional ?? [])].filter(
                (id): id is string => !!id,
            ),
        [pkg.media.images.cover, pkg.media.images.additional],
    );
    const [activeIdx, setActiveIdx] = useState(0);
    const safeActiveIdx = activeIdx < mediaIds.length ? activeIdx : 0;
    const active = mediaIds[safeActiveIdx];

    const onKeyDown = (e: JSX.TargetedKeyboardEvent<HTMLDivElement>) => {
        if (mediaIds.length < 2) return;
        if (e.key === 'ArrowRight') {
            e.preventDefault();
            setActiveIdx((i) => (i + 1) % mediaIds.length);
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            setActiveIdx((i) => (i - 1 + mediaIds.length) % mediaIds.length);
        }
    };

    return (
        <div onKeyDown={onKeyDown}>
            {active ? (
                <HeroFrame
                    mediaId={active}
                    altText={pkg.name ?? pkg.kind}
                    isPaywall={isPaywall}
                    t={t}
                />
            ) : (
                <NoImagePlate
                    kind={pkg.kind}
                    isPaywall={isPaywall}
                    t={t}
                />
            )}
            {mediaIds.length > 1 && (
                <ul
                    class="mt-3 grid grid-cols-5 gap-2"
                    role="tablist"
                    aria-label={pkg.name ?? pkg.kind}
                >
                    {mediaIds.map((id, idx) => {
                        const isActive = idx === safeActiveIdx;
                        return (
                            <li key={id}>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    onClick={() => setActiveIdx(idx)}
                                    class={`block w-full overflow-hidden rounded border transition ${
                                        isActive
                                            ? 'border-accent ring-2 ring-accent-glow'
                                            : 'border-rule hover:border-rule-strong'
                                    }`}
                                >
                                    <img
                                        src={withBase(`/media/${encodeURIComponent(id)}/package-sighting`)}
                                        alt=""
                                        loading="lazy"
                                        decoding="async"
                                        class="block aspect-[4/5] w-full object-cover"
                                    />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

interface HeroFrameProps {
    mediaId: string;
    altText: string;
    isPaywall: boolean;
    t: ReturnType<typeof useTranslation>;
}

// The hero frame is aspect-[4/5] and the cover fills it, so the rendered image
// width is max(frameWidth, frameHeight x sourceAspect). `sizes` reports that
// CSS width; the browser multiplies by devicePixelRatio to pick a srcset rung.
// Before the source aspect is known we assume a landscape-ish cover so the
// first paint over-fetches rather than under-fetches.
function computeHeroSizes(
    frameWidth: number | null,
    natural: { w: number; h: number } | null,
): string {
    if (!frameWidth) return '100vw';
    const frameHeight = (frameWidth * 5) / 4; // aspect-[4/5] => height = width * 5/4
    const aspect = natural && natural.h > 0 ? natural.w / natural.h : 1.6;
    const coverWidth = Math.max(frameWidth, frameHeight * aspect);
    return `${Math.ceil(coverWidth)}px`;
}

function HeroFrame({ mediaId, altText, isPaywall, t }: HeroFrameProps) {
    const frameRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
    const [breathing, setBreathing] = useState(false);
    const [frameWidth, setFrameWidth] = useState<number | null>(null);
    const motionMode = useMotionMode();

    useEffect(() => {
        setNatural(null);
        setBreathing(false);
        if (imgRef.current) {
            imgRef.current.style.transform = 'translate3d(-50%, -50%, 0)';
        }
    }, [mediaId]);

    // Measure the rendered frame width so the hero srcset/sizes request a rung
    // matched to the actual display size x devicePixelRatio.
    useEffect(() => {
        const el = frameRef.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect.width;
            if (w) setFrameWidth(w);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        if (motionMode !== 'pan') return;
        if (!natural || !frameRef.current || !imgRef.current) return;
        const rect = frameRef.current.getBoundingClientRect();
        const overpan = computeOverpan(natural, rect.width, rect.height);
        if (overpan.x < 8 && overpan.y < 8) return;
        const targetX = -overpan.x * 0.28;
        const targetY = -overpan.y * 0.28;
        const img = imgRef.current;
        setBreathing(true);
        img.style.setProperty('--hero-pan-dur', '1400ms');
        const driftId = window.setTimeout(() => {
            img.style.transform = `translate3d(calc(-50% + ${targetX.toFixed(1)}px), calc(-50% + ${targetY.toFixed(1)}px), 0)`;
        }, 80);
        const returnId = window.setTimeout(() => {
            img.style.transform = 'translate3d(-50%, -50%, 0)';
        }, 80 + 1400 + 400);
        const doneId = window.setTimeout(() => {
            setBreathing(false);
            img.style.removeProperty('--hero-pan-dur');
        }, 80 + 1400 + 400 + 1400 + 50);
        return () => {
            window.clearTimeout(driftId);
            window.clearTimeout(returnId);
            window.clearTimeout(doneId);
        };
    }, [natural, motionMode]);

    const onPointerMove = (e: JSX.TargetedPointerEvent<HTMLDivElement>) => {
        if (motionMode !== 'pan') return;
        if (breathing) return;
        if (!natural || !frameRef.current || !imgRef.current) return;
        const rect = frameRef.current.getBoundingClientRect();
        const cx = (e.clientX - rect.left) / rect.width;
        const cy = (e.clientY - rect.top) / rect.height;
        applyPan(imgRef.current, natural, rect.width, rect.height, cx, cy);
    };
    const onPointerEnter = () => {
        if (motionMode !== 'pan' || !imgRef.current) return;
        imgRef.current.style.setProperty('--hero-pan-dur', '120ms');
    };
    const onPointerLeave = () => {
        if (motionMode !== 'pan' || !imgRef.current) return;
        imgRef.current.style.setProperty('--hero-pan-dur', '500ms');
        imgRef.current.style.transform = 'translate3d(-50%, -50%, 0)';
    };

    // Responsive hero ladder. Static covers get the full 1024/1600/2400 range;
    // animated/motion heroes stay capped at 1280/1600 because large widths
    // multiply per-frame encode cost. The browser picks a rung from `sizes` x
    // devicePixelRatio; no-upscale means a rung wider than the source just
    // serves the source's native size.
    const heroLadder: ReadonlyArray<readonly [string, number]> =
        motionMode === 'static'
            ? [
                  ['package-hero-static-1024', 1024],
                  ['package-hero-static-1600', 1600],
                  ['package-hero-static-2400', 2400],
              ]
            : [
                  ['package-hero-1280', 1280],
                  ['package-hero-1600', 1600],
              ];
    const heroSrcset = heroLadder
        .map(([recipe, w]) => `${withBase(`/media/${encodeURIComponent(mediaId)}/${recipe}`)} ${w}w`)
        .join(', ');
    // Largest rung as the src fallback (no-srcset browsers + initial paint).
    const heroUrl = withBase(
        `/media/${encodeURIComponent(mediaId)}/${heroLadder[heroLadder.length - 1][0]}`,
    );
    const heroSizes = computeHeroSizes(frameWidth, natural);

    const imgStyle = computeImgStyle(natural, motionMode);

    return (
        <div
            ref={frameRef}
            tabIndex={0}
            class={`relative aspect-[4/5] overflow-hidden rounded-md border border-rule bg-gradient-to-br from-surface-2 to-surface-3 outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                motionMode === 'pan' ? 'cursor-crosshair' : ''
            }`}
            onPointerEnter={onPointerEnter}
            onPointerMove={onPointerMove}
            onPointerLeave={onPointerLeave}
        >
            <img
                ref={imgRef}
                src={heroUrl}
                srcset={heroSrcset}
                sizes={heroSizes}
                alt={altText}
                loading="eager"
                decoding="async"
                draggable={false}
                onLoad={(e) => {
                    const img = e.currentTarget as HTMLImageElement;
                    setNatural({ w: img.naturalWidth, h: img.naturalHeight });
                }}
                class="absolute left-1/2 top-1/2 max-w-none select-none"
                style={{
                    ...imgStyle,
                    transform: 'translate3d(-50%, -50%, 0)',
                    transition: 'transform var(--hero-pan-dur, 200ms) cubic-bezier(0.22, 1, 0.36, 1)',
                    willChange: motionMode === 'pan' ? 'transform' : undefined,
                }}
            />
            {isPaywall && (
                <span class="pointer-events-none absolute right-3.5 top-3.5 inline-flex items-center gap-1 rounded-full border border-state-stale/45 bg-state-stale/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-state-stale">
                    {t('work.paywall.badge')}
                </span>
            )}
        </div>
    );
}

function NoImagePlate({
    kind,
    isPaywall,
    t,
}: {
    kind: string;
    isPaywall: boolean;
    t: ReturnType<typeof useTranslation>;
}) {
    return (
        <div class="relative aspect-[4/5] overflow-hidden rounded-md border border-rule bg-gradient-to-br from-surface-2 to-surface-3">
            <div class="absolute inset-0 grid place-items-center text-fg-faint">
                <span class="font-mono text-xs uppercase tracking-[0.18em]">{kind}</span>
            </div>
            {isPaywall && (
                <span class="absolute right-3.5 top-3.5 inline-flex items-center gap-1 rounded-full border border-state-stale/45 bg-state-stale/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-state-stale">
                    {t('work.paywall.badge')}
                </span>
            )}
        </div>
    );
}

type MotionMode = 'pan' | 'static';

function useMotionMode(): MotionMode {
    return useMemo<MotionMode>(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return 'pan';
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const coarsePointer = window.matchMedia('(hover: none)').matches;
        return reducedMotion || coarsePointer ? 'static' : 'pan';
    }, []);
}

function useIsDesktop(): boolean {
    const [isDesktop, setIsDesktop] = useState(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return true;
        return window.matchMedia('(min-width: 1024px)').matches;
    });

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(min-width: 1024px)');
        const onChange = () => setIsDesktop(mq.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    return isDesktop;
}

function computeOverpan(
    natural: { w: number; h: number },
    plateW: number,
    plateH: number,
): { x: number; y: number } {
    const aspectImg = natural.w / natural.h;
    const aspectPlate = plateW / plateH;
    let renderedW: number;
    let renderedH: number;
    if (aspectImg > aspectPlate) {
        renderedH = plateH;
        renderedW = plateH * aspectImg;
    } else {
        renderedW = plateW;
        renderedH = plateW / aspectImg;
    }
    return {
        x: Math.max(0, renderedW - plateW),
        y: Math.max(0, renderedH - plateH),
    };
}

function applyPan(
    img: HTMLImageElement,
    natural: { w: number; h: number },
    plateW: number,
    plateH: number,
    cursorX: number,
    cursorY: number,
): void {
    const { x: overpanX, y: overpanY } = computeOverpan(natural, plateW, plateH);
    const cx = Math.max(0, Math.min(1, cursorX));
    const cy = Math.max(0, Math.min(1, cursorY));
    const damp = 0.9;
    const panX = -overpanX * (cx - 0.5) * damp;
    const panY = -overpanY * (cy - 0.5) * damp;
    img.style.transform = `translate3d(calc(-50% + ${panX.toFixed(1)}px), calc(-50% + ${panY.toFixed(1)}px), 0)`;
}

function computeImgStyle(
    natural: { w: number; h: number } | null,
    motionMode: MotionMode,
): JSX.CSSProperties {
    if (motionMode === 'static') {
        return { width: '100%', height: '100%', objectFit: 'contain' };
    }
    if (!natural) {
        return { width: '100%', height: '100%', objectFit: 'cover' };
    }
    const aspectImg = natural.w / natural.h;
    const aspectPlate = 4 / 5;
    if (Math.abs(aspectImg - aspectPlate) < 0.02) {
        return { width: '100%', height: '100%', objectFit: 'cover' };
    }
    if (aspectImg > aspectPlate) {
        return { height: '100%', width: 'auto' };
    }
    return { width: '100%', height: 'auto' };
}

interface CollapsibleTextProps {
    text: string | null | undefined;
    maxHeightPx?: number | null;
    fadeFromClass?: string;
    className?: string;
}

function CollapsibleText({
    text,
    maxHeightPx,
    fadeFromClass = 'from-surface-base',
    className,
}: CollapsibleTextProps) {
    const t = useTranslation();
    const ref = useRef<HTMLParagraphElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [fullHeight, setFullHeight] = useState<number | null>(null);
    const motionMode = useMotionMode();

    useEffect(() => {
        const el = ref.current;
        if (!el || !text) return;
        const measure = () => setFullHeight(el.scrollHeight);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [text]);

    if (!text) return null;

    const useExplicitHeight = typeof maxHeightPx === 'number' && maxHeightPx > 0;
    const clampPx = useExplicitHeight ? maxHeightPx : null;

    let overflows = false;
    if (fullHeight !== null) {
        if (useExplicitHeight && clampPx !== null) {
            overflows = fullHeight > clampPx + 4;
        } else if (ref.current) {
            overflows = fullHeight > ref.current.clientHeight + 4;
        }
    }
    const showClamp = overflows && !expanded;
    const animMs = motionMode === 'static' ? 0 : 380;

    let wrapperStyle: JSX.CSSProperties | undefined;
    let useTailwindClampClass = false;
    if (useExplicitHeight && clampPx !== null) {
        wrapperStyle = {
            maxHeight: showClamp
                ? `${clampPx}px`
                : fullHeight !== null
                  ? `${fullHeight}px`
                  : undefined,
            overflow: 'hidden',
            transition: `max-height ${animMs}ms cubic-bezier(0.4, 0, 0.2, 1)`,
        };
    } else if (overflows && ref.current) {
        const clampedPx = ref.current.clientHeight;
        wrapperStyle = {
            maxHeight: showClamp ? `${clampedPx}px` : `${fullHeight}px`,
            overflow: 'hidden',
            transition: `max-height ${animMs}ms cubic-bezier(0.4, 0, 0.2, 1)`,
        };
        useTailwindClampClass = false;
    } else {
        useTailwindClampClass = !expanded;
    }

    return (
        <div class="relative mb-5">
            <p
                ref={ref}
                class={`whitespace-pre-line text-[14.5px] leading-relaxed text-fg-primary [text-wrap:pretty] ${
                    useTailwindClampClass ? 'line-clamp-6' : ''
                } ${className ?? ''}`}
                style={wrapperStyle}
            >
                {text}
            </p>
            {overflows && (
                <div
                    class={`pointer-events-none absolute inset-x-0 bottom-0 h-[3em] bg-gradient-to-t ${fadeFromClass} to-transparent`}
                    style={{
                        opacity: showClamp ? 1 : 0,
                        transition: `opacity ${animMs}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                    }}
                    aria-hidden="true"
                />
            )}
            {overflows && (
                <button
                    type="button"
                    onClick={() => setExpanded((e) => !e)}
                    aria-expanded={expanded}
                    class="mt-2 inline-flex items-baseline gap-1.5 text-[12.5px] text-fg-secondary outline-none transition-colors hover:text-fg-primary focus-visible:text-fg-primary"
                >
                    <span class="border-b border-dotted border-current pb-0.5">
                        {expanded
                            ? t('work.description.showLess')
                            : t('work.description.showMore')}
                    </span>
                    <span
                        class="text-[9px]"
                        style={{
                            display: 'inline-block',
                            transition: `transform ${animMs}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                        aria-hidden="true"
                    >
                        ▾
                    </span>
                </button>
            )}
        </div>
    );
}

function HeroSection({
    pkg,
    rel,
}: {
    pkg: Work;
    rel: ReturnType<typeof useRelativeTime>;
}) {
    const heroRef = useRef<HTMLDivElement>(null);
    const aboveRef = useRef<HTMLDivElement>(null);
    const belowRef = useRef<HTMLDivElement>(null);
    const [heroHeight, setHeroHeight] = useState<number | null>(null);
    const [aboveHeight, setAboveHeight] = useState<number | null>(null);
    const [belowHeight, setBelowHeight] = useState<number | null>(null);
    const isDesktop = useIsDesktop();

    useEffect(() => {
        if (!isDesktop) return;
        const observe = (el: HTMLElement | null, setter: (h: number) => void) => {
            if (!el) return null;
            setter(el.offsetHeight);
            const ro = new ResizeObserver(() => setter(el.offsetHeight));
            ro.observe(el);
            return ro;
        };
        const observers = [
            observe(heroRef.current, setHeroHeight),
            observe(aboveRef.current, setAboveHeight),
            observe(belowRef.current, setBelowHeight),
        ];
        return () => observers.forEach((ro) => ro?.disconnect());
    }, [isDesktop]);

    const allowance = useMemo(() => {
        if (!isDesktop) return null;
        if (heroHeight === null || aboveHeight === null || belowHeight === null) return null;
        const remaining = heroHeight - aboveHeight - belowHeight;
        return Math.max(remaining, 140);
    }, [isDesktop, heroHeight, aboveHeight, belowHeight]);

    return (
        <section class="grid grid-cols-1 items-start gap-[clamp(28px,4vw,56px)] lg:grid-cols-[minmax(0,1.05fr)_1fr]">
            <div ref={heroRef}>
                <HeroCover pkg={pkg} />
            </div>
            <InfoColumn
                pkg={pkg}
                rel={rel}
                aboveRef={aboveRef}
                belowRef={belowRef}
                descriptionMaxHeight={allowance}
            />
        </section>
    );
}

function InfoColumn({
    pkg,
    rel,
    aboveRef,
    belowRef,
    descriptionMaxHeight,
}: {
    pkg: Work;
    rel: ReturnType<typeof useRelativeTime>;
    aboveRef: RefObject<HTMLDivElement>;
    belowRef: RefObject<HTMLDivElement>;
    descriptionMaxHeight: number | null;
}) {
    const t = useTranslation();
    const primarySourceLabel = resolveSource(pkg.source).name;
    const updatedLabel = pkg.sightings.lastChangedAt
        ? rel(pkg.sightings.lastChangedAt)
        : rel(pkg.lastSeenAt);
    const canInstall = pkg.installable && (pkg.install.kind === 'Mirror' || pkg.install.kind === 'Upstream');
    const isPaywall = pkg.install.kind === 'Paywalled';
    const visitUrl = pkg.primary.page ?? pkg.install.url ?? undefined;

    return (
        <div>
            <div ref={aboveRef}>
                <div class="mb-2 flex items-center gap-2.5">
                    {pkg.source && (
                        <span class="inline-flex items-center gap-1.5 rounded-full border border-rule-strong bg-surface-1 px-2.5 py-0.5 text-[11.5px] text-fg-muted">
                            <SourceFavicon source={pkg.source} size={11} />
                            {primarySourceLabel}
                        </span>
                    )}
                    <span class="inline-flex items-center rounded-full border border-rule-strong px-2.5 py-0.5 text-[11.5px] text-fg-muted">
                        {pkg.kind}
                    </span>
                    <span class="font-mono text-[10.5px] uppercase tracking-[0.1em] text-fg-faint">
                        {t('work.updatedLabel', { when: updatedLabel })}
                    </span>
                </div>

                <h1 class="my-2 font-display text-[clamp(32px,4.2vw,52px)] font-normal leading-[1.04] tracking-tight text-fg-primary [text-wrap:balance]">
                    {pkg.name ?? '(unnamed)'}
                </h1>

                {pkg.author && (
                    <div class="my-5 flex items-baseline gap-2 border-y border-rule py-3 text-[15px] text-fg-muted">
                        <span class="text-[13px] text-fg-faint">{t('work.bylineBy')}</span>
                        <a
                            href={withBase(`/emporium?author=${encodeURIComponent(pkg.author)}`)}
                            class="italic text-fg-muted border-b border-dotted border-fg-faint transition-colors hover:text-accent hover:border-accent"
                            title={t('work.bylineLinkTitle', { author: pkg.author })}
                            aria-label={t('work.bylineLinkAria', { author: pkg.author })}
                        >
                            {pkg.author}
                        </a>
                    </div>
                )}

                <div class="mb-2 flex flex-wrap gap-2.5">
                    {/* Download is the primary, always-works path: a plain ZIP, no app
                        required. Install hands off to Gposingway Studio via the locked
                        gwpackage:// scheme - Studio is in development, so it is the
                        secondary, clearly-labelled option until a build ships. */}
                    {canInstall && pkg.install.url && (
                        <a
                            href={pkg.install.url}
                            class="inline-flex items-center gap-2 rounded-full border border-accent bg-accent px-5 py-2.5 text-sm font-medium text-surface-base transition-colors hover:bg-fg-primary hover:text-surface-base"
                        >
                            ↓ {t('work.install.download')}
                        </a>
                    )}
                    {canInstall && pkg.install.url && (
                        <a
                            href={gwpackageUrl(pkg.name || pkg.kind, pkg.install.url)}
                            title={t('work.install.studioHint')}
                            class="inline-flex items-center gap-2 rounded-full border border-rule-strong px-4 py-2.5 text-sm text-fg-primary transition-colors hover:border-fg-muted"
                        >
                            {t('work.install.install')}
                        </a>
                    )}
                    {visitUrl && (
                        <a
                            href={visitUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                            class={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm transition-colors ${canInstall ? 'border-rule-strong text-fg-primary hover:border-fg-muted' : 'border-accent bg-accent px-5 text-surface-base hover:bg-fg-primary hover:text-surface-base'}`}
                        >
                            ↗ {t('work.install.visitPage', { source: primarySourceLabel })}
                        </a>
                    )}
                    <LikeButton workId={pkg.id} initial={pkg.likedByMe ?? false} />
                    <CollectionsButton
                        workId={pkg.id}
                        initialInAny={pkg.inAnyOfMyCollections ?? false}
                    />
                </div>

                {canInstall && pkg.install.url && (
                    <p class="mb-5 text-[12.5px] leading-relaxed text-fg-muted">
                        {pkg.install.kind === 'Mirror'
                            ? t('work.install.mirrorNote')
                            : t('work.install.upstreamNote', { source: primarySourceLabel })}{' '}
                        <a
                            href={LINKS.github.installGuide}
                            target="_blank"
                            rel="noreferrer noopener"
                            class="border-b border-dotted border-fg-faint text-fg-secondary transition-colors hover:border-accent hover:text-accent"
                        >
                            {t('work.install.guideLink')}
                        </a>
                    </p>
                )}

                {isPaywall && (
                    <Notice tone="warn" badge={t('work.paywall.title')}>
                        {t('work.paywall.body', { source: primarySourceLabel })}
                    </Notice>
                )}
            </div>

            <CollapsibleText text={pkg.description} maxHeightPx={descriptionMaxHeight} />

            <div ref={belowRef}>
                <WorkTagChips categories={pkg.tags?.public?.categories} />
                <DateStrip pkg={pkg} />
                <Stats pkg={pkg} />
            </div>
        </div>
    );
}

function LikeButton({ workId, initial }: { workId: string; initial: boolean }) {
    const t = useTranslation();
    const me = useMe();
    const { liked, toggle, signedIn } = useWorkLike(workId, initial);
    // While the /me probe is in flight, render nothing rather than flashing a
    // misleading "sign in" ghost at an already-authenticated user.
    if (me.status === 'loading') return null;
    // Anonymous (or probe error): the strongest moment of intent shouldn't
    // render nothing. Show a ghost heart that nudges to sign-in and returns
    // here afterward. The nudge string is the accessible name (not just a
    // tooltip) so assistive tech announces where the link goes.
    if (!signedIn) {
        return (
            <a
                href={signInPath()}
                title={t('work.like.signInNudge')}
                aria-label={t('work.like.signInNudge')}
                class="inline-flex items-center gap-2 rounded-full border border-dashed border-rule px-4 py-2.5 text-sm text-fg-faint outline-none transition-colors hover:border-fg-muted hover:text-fg-secondary focus-visible:ring-2 focus-visible:ring-accent"
            >
                <DetailHeartIcon filled={false} />
                {t('work.like.action')}
            </a>
        );
    }
    return (
        <button
            type="button"
            onClick={toggle}
            aria-label={t(liked ? 'card.ariaUnlike' : 'card.ariaLike')}
            aria-pressed={liked}
            class={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm transition-colors ${liked ? 'border-cool bg-cool/10 text-cool hover:bg-cool/20' : 'border-rule-strong text-fg-muted hover:border-fg-muted hover:text-fg-primary'}`}
        >
            <DetailHeartIcon filled={liked} />
            {t(liked ? 'work.like.done' : 'work.like.action')}
        </button>
    );
}

function DetailHeartIcon({ filled }: { filled: boolean }): JSX.Element {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill={filled ? 'currentColor' : 'none'}
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
        >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
    );
}

/**
 * Renders every Public tag axis from <see cref="Work.tags"/> as a grouped
 * chip strip. Each axis (game, engine, technique, access, compatibility,
 * type, locale) gets a label-prefixed chip cluster - the lexical engine's
 * AddTag actions land here and the operator can spot which classifiers
 * fired on a given Work at a glance. The visible set is governed
 * server-side by WorkDenormalizer.AllowedPublicTagAxes; provider-only
 * axes (host, source) are not rolled up, so a render-side omit list is no
 * longer needed - the source-favicon chip handles brand identity from the
 * flat Work.source field.
 */
function WorkTagChips({ categories }: { categories?: Record<string, string[]> | null }) {
    if (!categories) return null;
    const entries = Object.entries(categories)
        .filter(([, vals]) => Array.isArray(vals) && vals.length > 0)
        .sort(([a], [b]) => a.localeCompare(b));
    if (entries.length === 0) return null;
    return (
        <div class="mb-5 flex flex-col gap-2">
            {entries.map(([axis, vals]) => (
                <div key={axis} class="flex flex-wrap items-center gap-1.5">
                    <span class="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-faint">
                        {axis}
                    </span>
                    {vals.map((tag) => (
                        <span
                            key={tag}
                            class="inline-flex items-center rounded-full border border-rule bg-surface-base px-2 py-0.5 text-[11px] text-fg-secondary"
                        >
                            {tag}
                        </span>
                    ))}
                </div>
            ))}
        </div>
    );
}

function DateStrip({ pkg }: { pkg: Work }) {
    const t = useTranslation();
    const fmt = useDateFormat();
    const formatIso = (iso: string | null | undefined): string => (iso ? fmt(iso) : '');
    const cells: Array<{ label: string; value: string }> = [];
    // Prefer the upstream-sourced dates when the denormalizer has populated them; fall back to
    // the platform-side timeline (discoveredAt + lastChangedAt) so orphaned-data works still
    // render a meaningful three-column strip instead of a single "Last seen" island.
    if (pkg.upstream.createdAt) {
        cells.push({
            label: t('work.dates.upstreamCreated'),
            value: formatIso(pkg.upstream.createdAt),
        });
    } else {
        cells.push({
            label: t('work.dates.discovered'),
            value: formatIso(pkg.discoveredAt),
        });
    }
    if (pkg.upstream.updatedAt) {
        cells.push({
            label: t('work.dates.upstreamUpdated'),
            value: formatIso(pkg.upstream.updatedAt),
        });
    } else if (pkg.sightings.lastChangedAt) {
        cells.push({
            label: t('work.dates.lastChanged'),
            value: formatIso(pkg.sightings.lastChangedAt),
        });
    }
    cells.push({ label: t('work.dates.lastSeen'), value: formatIso(pkg.lastSeenAt) });
    if (cells.length === 0) return null;
    return (
        <dl class="mb-5 grid grid-cols-3 gap-x-6 gap-y-1 border-t border-rule pt-4">
            {cells.map((c) => (
                <div key={c.label}>
                    <dt class="font-mono text-[10.5px] uppercase tracking-[0.12em] text-fg-faint">
                        {c.label}
                    </dt>
                    <dd class="mt-0.5 text-[13px] text-fg-muted">{c.value}</dd>
                </div>
            ))}
        </dl>
    );
}

function Notice({
    tone,
    badge,
    children,
}: {
    tone: 'warn' | 'warm';
    badge: string;
    children: ComponentChildren;
}) {
    const colors =
        tone === 'warm'
            ? 'border-[#d49254]/40 bg-[#d49254]/[0.06] text-[#d49254]'
            : 'border-state-stale/40 bg-state-stale/[0.06] text-state-stale';
    return (
        <div
            class={`mb-4 flex items-start gap-2.5 rounded border px-3.5 py-3 text-[12.5px] leading-snug ${colors}`}
        >
            <span class="mt-0.5">▲</span>
            <div class="text-fg-muted">
                <b class={tone === 'warm' ? 'text-[#d49254]' : 'text-state-stale'}>{badge}</b>{' '}
                <span class="text-fg-muted">{children}</span>
            </div>
        </div>
    );
}

function Stats({ pkg }: { pkg: Work }) {
    const t = useTranslation();
    const cells: Array<{ label: string; value: string }> = [];
    if (pkg.sightings.count > 0)
        cells.push({ label: t('work.stats.views'), value: String(pkg.sightings.count) });
    if (cells.length === 0) return null;
    const cols =
        cells.length === 1 ? 'grid-cols-1' : cells.length === 2 ? 'grid-cols-2' : 'grid-cols-3';
    return (
        <div class={`grid ${cols} overflow-hidden rounded border border-rule`}>
            {cells.map((c) => (
                <StatCell key={c.label} label={c.label} value={c.value} />
            ))}
        </div>
    );
}

function StatCell({ label, value }: { label: string; value: string }) {
    return (
        <div class="border-r border-rule px-3.5 py-3 last:border-r-0">
            <div class="mb-1 font-mono text-[10.5px] uppercase tracking-[0.16em] text-fg-faint">
                {label}
            </div>
            <div class="font-display text-[22px] italic text-fg-primary">{value}</div>
        </div>
    );
}

function AvailableOnPanel({
    pkg,
    sourcesBySlug,
}: {
    pkg: Work;
    sourcesBySlug?: Map<string, Provider>;
}) {
    const t = useTranslation();
    if (pkg.sightings.scoutSlugs.length <= 1) return null;
    const primary = pkg.primary.scoutSlug;
    const others = pkg.sightings.scoutSlugs.filter((s) => s !== primary);
    if (others.length === 0) return null;
    return (
        <section class="mt-14 rounded-md border border-rule bg-surface-raised px-6 py-5">
            <div
                class="grid items-center gap-6"
                style={{ gridTemplateColumns: 'auto 1fr auto' }}
            >
                <div>
                    <Eyebrow tone="default">
                        <span class="text-accent">{t('work.alsoOn.eyebrow')}</span>
                    </Eyebrow>
                    <div class="mt-1 text-[19px] italic text-fg-primary">
                        {t('work.alsoOn.title', { n: pkg.sightings.scoutSlugs.length })}
                    </div>
                </div>
                <div class="flex flex-wrap gap-2">
                    {others.map((slug) => {
                        const provider = sourcesBySlug?.get(slug);
                        const brandSource = provider?.source ?? slug;
                        return (
                            <span
                                key={slug}
                                class="inline-flex items-center gap-1.5 rounded-full border border-rule-strong bg-surface-base px-2.5 py-0.5 text-[11.5px] text-fg-muted"
                            >
                                <SourceFavicon source={brandSource} size={11} />
                                {resolveSource(brandSource).name}
                            </span>
                        );
                    })}
                </div>
                <span class="font-mono text-[10.5px] uppercase tracking-[0.16em] text-fg-faint">
                    {t('work.alsoOn.verifiedByGwtag')}
                </span>
            </div>
        </section>
    );
}

function SightingsList({
    sightings,
    sourcesBySlug,
}: {
    pkg: Work;
    sightings: { status: string; data?: Sighting[] };
    sourcesBySlug?: Map<string, Provider>;
}) {
    const rel = useRelativeTime();
    if (sightings.status !== 'success' || !sightings.data || sightings.data.length === 0)
        return null;
    return (
        <section class="mt-14">
            <Eyebrow>SEEN ON - full sighting log</Eyebrow>
            <ul class="mt-3 space-y-2.5 text-sm">
                {sightings.data.map((s) => {
                    const provider = Array.from(sourcesBySlug?.values() ?? []).find(
                        (src) => src.id === s.scoutId,
                    );
                    const brandSource = provider?.source ?? '';
                    const brandName = resolveSource(brandSource).name;
                    return (
                        <li
                            key={s.id}
                            class="flex items-center gap-2 border-b border-rule pb-2.5"
                        >
                            {brandSource && (
                                <SourceFavicon
                                    source={brandSource}
                                    size={14}
                                    title={brandName}
                                />
                            )}
                            <span class="text-xs font-medium text-fg-secondary">
                                {brandName}
                            </span>
                            <a
                                href={s.url}
                                target="_blank"
                                rel="noreferrer noopener"
                                class="truncate text-fg-primary hover:text-accent"
                                title={s.name ?? s.sourceItemId}
                            >
                                {s.name ?? s.sourceItemId} ↗
                            </a>
                            <span class="ml-auto font-mono text-[10.5px] uppercase tracking-[0.06em] text-fg-faint">
                                {rel(s.discoveredAt)}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

function RelatedSection({
    pkg,
    related,
}: {
    pkg: Work;
    related: WorkSummary[];
}) {
    const t = useTranslation();
    const rel = useRelativeTime();
    const me = useMe();
    const viewerIsAdmin = me.status === 'authenticated' && isAdmin(me.user);

    // `related` is already self-excluded, threshold-ranked, visibility-filtered and capped by the
    // server (GET /api/works/{id}/related) - render it directly, no client-side re-filter.
    if (related.length === 0) return null;

    return (
        <section class="mt-14">
            <div class="mb-3.5 flex items-baseline justify-between">
                <Eyebrow>{t('work.related.eyebrow')}</Eyebrow>
                {pkg.author && (
                    <span class="font-mono text-[10.5px] uppercase tracking-[0.16em] text-fg-muted">
                        {t('work.related.moreByCreator', { creator: pkg.author })} →
                    </span>
                )}
            </div>
            <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {related.map((p) => {
                    const model = toCardModel(p, {
                        formatRelative: rel,
                        viewerIsAdmin,
                    });
                    return (
                        <a
                            key={p.id}
                            href={withBase(workUrl(p))}
                            class="block"
                        >
                            <UiWorkCard pkg={model} density="compact" />
                        </a>
                    );
                })}
            </div>
        </section>
    );
}

/**
 * Admin-only action panel rendered between Sightings and Related. Visible
 * only when the signed-in user carries the admin role; anonymous and
 * non-admin users see nothing. Surfaces the Work-scoped admin
 * affordances inline so an admin can act without context-switching to
 * the admin shell:
 *
 *  - Suppress / Unsuppress with a reason note (writes
 *    <c>Work.Suppression</c>; ResolveStatus flips status downstream).
 *  - Pin / Unpin the metadata so the denormalizer leaves admin-edited
 *    name / author / description alone on the next refresh.
 *  - Recrawl every Sighting attached to this Work in ReExtract
 *    Overwrite mode - refreshes description / cover / archive URL from
 *    upstream without waiting for the scheduler.
 *
 * Diagnostic strip on the right shows the stable id + slug + chain + role
 * resolution so admins inspecting a row don't have to crack open the
 * Mongo dump.
 */
function AdminPanel({
    pkg,
    sightings,
    onMutated,
}: {
    pkg: Work;
    sightings: Sighting[];
    onMutated: () => void;
}) {
    const me = useMe();
    const meUser = me.status === 'authenticated' ? me.user : undefined;
    type BusyKey = 'suppress' | 'unsuppress' | 'pin' | 'unpin' | 'recrawl' | 'forceMerge' | 'forceSplit' | 'operatorSplit';
    const [busy, setBusy] = useState<BusyKey | null>(null);
    const [reason, setReason] = useState('');
    const [feedback, setFeedback] = useState<{ tone: 'ok' | 'error'; msg: string } | null>(null);

    const [forceMergeOpen, setForceMergeOpen] = useState(false);
    const [forceMergeTarget, setForceMergeTarget] = useState('');
    const [forceSplitOpen, setForceSplitOpen] = useState(false);
    const [forceSplitOther, setForceSplitOther] = useState('');
    const [forceSplitNote, setForceSplitNote] = useState('');
    const [splitOpen, setSplitOpen] = useState(false);
    const [splitSelected, setSplitSelected] = useState<Set<string>>(new Set());
    const [splitWriteForceSplits, setSplitWriteForceSplits] = useState(true);
    const [splitNote, setSplitNote] = useState('');

    // Every hook is declared above; the admin-only gate renders nothing for
    // non-admins and MUST sit after all hooks - `me` resolves loading ->
    // authenticated, so a hook below this guard would change the hook count
    // across renders for an admin (Rules of Hooks).
    if (!isAdmin(meUser)) return null;

    const isSuppressed = pkg.status === 'Suppressed';
    const isPinned = pkg.metadataPinned ?? false;

    const run = async <T,>(
        key: BusyKey,
        op: () => Promise<T>,
        successMsg: string,
    ) => {
        setBusy(key);
        setFeedback(null);
        try {
            await op();
            setFeedback({ tone: 'ok', msg: successMsg });
            onMutated();
        } catch (e) {
            setFeedback({ tone: 'error', msg: e instanceof Error ? e.message : String(e) });
        } finally {
            setBusy(null);
        }
    };

    const submitForceMerge = async () => {
        const target = forceMergeTarget.trim();
        if (!target) return;
        await run(
            'forceMerge',
            () => api.admin.convergence.forceMerge(pkg.id, { targetWorkId: target }),
            `Force-merged into ${target}. This package's id now 301-redirects.`,
        );
        setForceMergeOpen(false);
        setForceMergeTarget('');
    };

    const submitForceSplit = async () => {
        const other = forceSplitOther.trim();
        if (!other) return;
        await run(
            'forceSplit',
            () =>
                api.admin.convergence.forceSplit({
                    workIdA: pkg.id,
                    workIdB: other,
                    note: forceSplitNote.trim() || undefined,
                }),
            `Force-split recorded against ${other}.`,
        );
        setForceSplitOpen(false);
        setForceSplitOther('');
        setForceSplitNote('');
    };

    const openOperatorSplit = () => {
        setSplitSelected(new Set());
        setSplitWriteForceSplits(true);
        setSplitNote('');
        setSplitOpen(true);
    };

    const submitOperatorSplit = async () => {
        const ids = Array.from(splitSelected);
        if (ids.length === 0) return;
        await run(
            'operatorSplit',
            async () => {
                const result = await api.admin.works.split(pkg.id, {
                    sightingIds: ids,
                    writeForceSplits: splitWriteForceSplits,
                    note: splitNote.trim() || null,
                });
                return result;
            },
            `Split ${ids.length} sighting${ids.length === 1 ? '' : 's'} into a fresh Work.`,
        );
        setSplitOpen(false);
        setSplitSelected(new Set());
        setSplitWriteForceSplits(true);
        setSplitNote('');
    };

    const toggleSuppress = () => {
        if (isSuppressed) {
            return run('unsuppress', () => api.admin.works.unsuppress(pkg.id), 'Suppression cleared.');
        }
        return run(
            'suppress',
            () => api.admin.works.suppress(pkg.id, {
                code: 'admin',
                detail: reason.trim() || 'Suppressed via package detail panel.',
            }),
            'Work suppressed.',
        );
    };

    const togglePin = () => {
        if (isPinned) {
            return run('unpin', () => api.admin.works.unpin(pkg.id), 'Metadata unpinned - denormalizer will overwrite from primary sighting again.');
        }
        if (!pkg.name) {
            setFeedback({ tone: 'error', msg: 'Cannot pin: Work has no Name to lock.' });
            return;
        }
        return run(
            'pin',
            () => api.admin.works.pin(pkg.id, {
                name: pkg.name!,
                author: pkg.author ?? undefined,
                description: pkg.description ?? undefined,
            }),
            'Metadata pinned - current name/author/description locked.',
        );
    };

    const recrawl = () => {
        const ids = sightings.map((s) => s.id);
        if (ids.length === 0) {
            setFeedback({ tone: 'error', msg: 'No sightings to recrawl.' });
            return;
        }
        return run(
            'recrawl',
            () =>
                api.admin.maintenance.recrawl({
                    mode: 'ReExtract',
                    sightingIds: ids,
                    mergePolicy: 'Overwrite',
                    bypassHttpCache: true,
                    bypassRejectionCache: true,
                }),
            `Recrawl fired for ${ids.length} sighting(s) - refresh in a few seconds.`,
        );
    };

    const btnBase = 'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
    const btnPrimary = `${btnBase} border-rule-strong text-fg-primary hover:border-accent hover:text-accent`;
    const btnDanger = `${btnBase} border-state-failed text-state-failed hover:bg-state-failed hover:text-bg-base`;
    const btnWarn = `${btnBase} border-state-stale text-state-stale hover:bg-state-stale hover:text-bg-base`;

    return (
        <section
            class="mt-14 rounded-lg border border-rule-strong bg-surface-veil/40 px-5 py-4"
            aria-label="Admin actions"
        >
            <div class="mb-3 flex items-baseline gap-2">
                <span class="font-mono text-[10px] uppercase tracking-[0.18em] text-accent">
                    ADMIN
                </span>
                <span class="text-[11.5px] text-fg-faint">
                    Work-scoped moderation. Visible to admin role only.
                </span>
            </div>

            <div class="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={toggleSuppress}
                    disabled={busy !== null}
                    class={isSuppressed ? btnPrimary : btnDanger}
                    title={isSuppressed ? 'Clear suppression - Work returns to Published.' : 'Hide from public catalog.'}
                >
                    {busy === 'suppress' || busy === 'unsuppress'
                        ? '...'
                        : isSuppressed
                            ? '↺ Unsuppress'
                            : '⊘ Suppress'}
                </button>

                <button
                    type="button"
                    onClick={togglePin}
                    disabled={busy !== null}
                    class={isPinned ? btnPrimary : btnWarn}
                    title={isPinned
                        ? 'Unpin - denormalizer will overwrite Name/Author/Description from the primary sighting again.'
                        : 'Pin - denormalizer leaves Name/Author/Description alone on refresh.'}
                >
                    {busy === 'pin' || busy === 'unpin'
                        ? '...'
                        : isPinned
                            ? '📌 Unpin metadata'
                            : '📌 Pin metadata'}
                </button>

                <button
                    type="button"
                    onClick={recrawl}
                    disabled={busy !== null || sightings.length === 0}
                    class={btnPrimary}
                    title={`Re-extract description / cover / archive from upstream for ${sightings.length} sighting(s).`}
                >
                    {busy === 'recrawl' ? '...' : `⟲ Recrawl (${sightings.length})`}
                </button>

                <button
                    type="button"
                    onClick={() => setForceMergeOpen(true)}
                    disabled={busy !== null}
                    class={btnDanger}
                    title="Retire this Work into another. The retired id 301-redirects forever; sightings reassign to the survivor."
                >
                    {busy === 'forceMerge' ? '...' : '⇆ Force-merge into…'}
                </button>

                <button
                    type="button"
                    onClick={() => setForceSplitOpen(true)}
                    disabled={busy !== null}
                    class={btnWarn}
                    title="Lock this Work open against another so the scored pass never auto-merges them."
                >
                    {busy === 'forceSplit' ? '...' : '⊥ Force-split from…'}
                </button>

                <button
                    type="button"
                    onClick={openOperatorSplit}
                    disabled={busy !== null || sightings.length < 2}
                    class={btnWarn}
                    title={sightings.length < 2
                        ? 'Need at least 2 Sightings to split a subset off.'
                        : 'Peel a chosen subset of Sightings off into a freshly-minted Work.'}
                >
                    {busy === 'operatorSplit' ? '...' : `✂ Split sightings…`}
                </button>

                {!isSuppressed && (
                    <input
                        type="text"
                        value={reason}
                        onInput={(e) => setReason((e.target as HTMLInputElement).value)}
                        placeholder="Suppression reason (optional)"
                        class="min-w-[240px] flex-1 rounded-full border border-rule bg-surface-raised px-3 py-1.5 text-[12px] text-fg-primary placeholder:text-fg-faint focus:border-accent focus:outline-none"
                    />
                )}
            </div>

            {feedback && (
                <p
                    class={`mt-2.5 font-mono text-[11px] ${
                        feedback.tone === 'ok' ? 'text-state-ready' : 'text-state-failed'
                    }`}
                >
                    {feedback.msg}
                </p>
            )}

            <dl class="mt-4 grid grid-cols-1 gap-x-6 gap-y-1 border-t border-rule pt-3 text-[11px] sm:grid-cols-2 lg:grid-cols-4">
                <DiagRow label="Work id" value={pkg.id} mono />
                <DiagRow label="Slug" value={pkg.slug ?? '-'} mono />
                <DiagRow label="Status" value={pkg.status} />
                <DiagRow label="Install kind" value={pkg.install?.kind ?? '-'} />
                <DiagRow label="Sources" value={(pkg.sightings.scoutSlugs ?? []).join(', ') || '-'} mono wide />
                <DiagRow label="Sightings" value={String(sightings.length)} />
                <DiagRow
                    label="Claimed by"
                    value={
                        pkg.claimedByUserIds && pkg.claimedByUserIds.length > 0
                            ? pkg.claimedByUserIds.join(', ')
                            : '-'
                    }
                    mono
                />
                {pkg.suppression && (
                    <DiagRow
                        label="Suppression"
                        value={`${pkg.suppression.code} - ${pkg.suppression.detail ?? ''}`.trim()}
                        wide
                    />
                )}
            </dl>

            <ConfirmModal
                open={forceMergeOpen}
                onClose={() => busy === null && setForceMergeOpen(false)}
                onConfirm={submitForceMerge}
                title="Force-merge into target Work?"
                body={`This Work's id (${pkg.id}) will retire and 301-redirect to the target forever. Every Sighting reassigns. Aliases follow. Irreversible without backup restore.`}
                mode="form"
                confirmLabel="Force-merge"
                confirmVariant="danger"
                confirmDisabled={forceMergeTarget.trim().length === 0}
                busy={busy === 'forceMerge'}
            >
                <label class="block">
                    <span class="block font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted">
                        Target Work ID <span class="text-state-failed">*</span>
                    </span>
                    <input
                        type="text"
                        value={forceMergeTarget}
                        onInput={(e) => setForceMergeTarget((e.target as HTMLInputElement).value)}
                        placeholder="e.g. pkg-7f3a…"
                        autoFocus
                        spellcheck={false}
                        class="mt-1 w-full rounded border border-rule bg-surface-base px-3 py-1.5 font-mono text-[12px] text-fg-primary outline-none focus:border-accent"
                    />
                </label>
            </ConfirmModal>

            <ConfirmModal
                open={forceSplitOpen}
                onClose={() => busy === null && setForceSplitOpen(false)}
                onConfirm={submitForceSplit}
                title="Force-split from another Work?"
                body="The two Works will never auto-merge again even when their match signals overlap. Use this when the scored pass keeps proposing a merge for genuinely distinct works."
                mode="form"
                confirmLabel="Record force-split"
                confirmDisabled={forceSplitOther.trim().length === 0}
                busy={busy === 'forceSplit'}
            >
                <label class="block">
                    <span class="block font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted">
                        Other Work ID <span class="text-state-failed">*</span>
                    </span>
                    <input
                        type="text"
                        value={forceSplitOther}
                        onInput={(e) => setForceSplitOther((e.target as HTMLInputElement).value)}
                        placeholder="e.g. pkg-2c91…"
                        autoFocus
                        spellcheck={false}
                        class="mt-1 w-full rounded border border-rule bg-surface-base px-3 py-1.5 font-mono text-[12px] text-fg-primary outline-none focus:border-accent"
                    />
                </label>
                <label class="block">
                    <span class="block font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted">
                        Note (optional)
                    </span>
                    <input
                        type="text"
                        value={forceSplitNote}
                        onInput={(e) => setForceSplitNote((e.target as HTMLInputElement).value)}
                        placeholder="e.g. same author, different editions"
                        class="mt-1 w-full rounded border border-rule bg-surface-base px-3 py-1.5 text-sm text-fg-primary outline-none focus:border-accent"
                    />
                </label>
            </ConfirmModal>

            <ConfirmModal
                open={splitOpen}
                onClose={() => busy === null && setSplitOpen(false)}
                onConfirm={submitOperatorSplit}
                title={`Split ${splitSelected.size} sighting${splitSelected.size === 1 ? '' : 's'} into a new Work?`}
                body="The selected Sightings detach from this Work and attach to a freshly-minted one. Both ids keep resolving. Useful when convergence merged genuinely distinct sources into one Work."
                mode="form"
                confirmLabel="Split"
                confirmVariant="danger"
                confirmDisabled={splitSelected.size === 0 || splitSelected.size === sightings.length}
                busy={busy === 'operatorSplit'}
            >
                <div class="space-y-3">
                    <div>
                        <div class="mb-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted">
                            Sightings to move ({splitSelected.size}/{sightings.length})
                        </div>
                        <div class="max-h-[280px] overflow-y-auto rounded border border-rule bg-surface-base">
                            {sightings.length === 0 && (
                                <p class="px-3 py-2 text-xs text-fg-faint">No sightings available.</p>
                            )}
                            {sightings.map((s) => {
                                const checked = splitSelected.has(s.id);
                                return (
                                    <label
                                        key={s.id}
                                        class="flex cursor-pointer items-start gap-2 border-t border-rule px-3 py-2 first:border-t-0 hover:bg-surface-veil/40"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => {
                                                const next = new Set(splitSelected);
                                                if (checked) next.delete(s.id);
                                                else next.add(s.id);
                                                setSplitSelected(next);
                                            }}
                                            class="mt-1"
                                        />
                                        <div class="min-w-0 flex-1">
                                            <div class="font-mono text-[11px] text-fg-primary">{s.id}</div>
                                            <div class="text-[10.5px] text-fg-muted">
                                                {s.scoutId || '(no scout)'} · {s.url || '(no url)'}
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                        {splitSelected.size === sightings.length && sightings.length > 0 && (
                            <p class="mt-1 text-[10.5px] text-state-failed">
                                Splitting every Sighting would empty this Work. Keep at least one.
                            </p>
                        )}
                    </div>

                    <label class="flex items-start gap-2 text-xs text-fg-muted">
                        <input
                            type="checkbox"
                            checked={splitWriteForceSplits}
                            onChange={(e) =>
                                setSplitWriteForceSplits((e.target as HTMLInputElement).checked)
                            }
                            class="mt-0.5"
                        />
                        <span>
                            Write pairwise ForceSplit constraints between the kept and moved
                            source-ids so the next convergence pass doesn't undo the split.
                            Leave on unless you want the split to be advisory only.
                        </span>
                    </label>

                    <label class="block">
                        <span class="block font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted">
                            Note (optional)
                        </span>
                        <input
                            type="text"
                            value={splitNote}
                            onInput={(e) => setSplitNote((e.target as HTMLInputElement).value)}
                            placeholder="e.g. convergence false-positive, different chapters"
                            class="mt-1 w-full rounded border border-rule bg-surface-base px-3 py-1.5 text-sm text-fg-primary outline-none focus:border-accent"
                        />
                    </label>
                </div>
            </ConfirmModal>
        </section>
    );
}

function DiagRow({
    label,
    value,
    mono,
    wide,
}: {
    label: string;
    value: string;
    mono?: boolean;
    wide?: boolean;
}) {
    return (
        <div class={`flex items-baseline gap-2 ${wide ? 'sm:col-span-2 lg:col-span-4' : ''}`}>
            <dt class="font-mono text-[9.5px] uppercase tracking-[0.16em] text-fg-faint">
                {label}
            </dt>
            <dd class={`truncate text-fg-secondary ${mono ? 'font-mono text-[10.5px]' : ''}`} title={value}>
                {value}
            </dd>
        </div>
    );
}
